/* import __COLOCATED_TEMPLATE__ from './show-launcher.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
// @ts-expect-error No types declaration for ember-copy yet.
import { copy } from 'ember-copy';
import { EXTRA_USER_ATTRIBUTES } from 'embercom/lib/messenger/installation-constants';
import type AppService from 'embercom/services/app-service';
import type ArrayProxy from '@ember/array/proxy';
import type Model from '@ember-data/model';
import type EmberObject from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import { type Predicate } from 'embercom/lib/knowledge-hub/constants';
import type MessengerSettingsTargetUserProvider from 'embercom/services/messenger-settings-target-user-provider';

import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
// @ts-ignore
import PredicateGroup from 'predicates/models/predicate-group';

export interface Args {
  isMobilePlatform: boolean;
  visibilitySettings: Model & {
    messengerEnabled: boolean;
    launcherPredicates: ArrayProxy<Predicate>;
    launcherPredicatesForAnonymous: ArrayProxy<Predicate>;
    launcherPredicatesForUsers: ArrayProxy<Predicate>;
  };
}

export default class ShowLauncherSettingsComponent extends Component<Args> {
  @service declare appService: AppService;
  @service declare attributeService: $TSFixMe;
  @service declare messengerSettingsTargetUserProvider: MessengerSettingsTargetUserProvider;
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked launcherPredicateGroupForAnonymous: PredicateGroup | null = null;
  @tracked launcherPredicateGroupForUsers: PredicateGroup | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.initLauncherPredicateGroups();
  }

  @action
  initLauncherPredicateGroups() {
    let rawPredicatesForAnonymous = this.launcherPredicatesForAnonymous.map((p: $TSFixMe) => {
      return p.serialize ? p.serialize() : p;
    });
    let rawPredicatesForUsers = this.launcherPredicatesForUsers.map((p: $TSFixMe) => {
      return p.serialize ? p.serialize() : p;
    });

    this.launcherPredicateGroupForAnonymous = this.store.createFragment(
      'predicates/predicate-group',
      {
        predicates: PredicateGroup.convertRawPredicates(this.store, rawPredicatesForAnonymous),
      },
    );
    this.launcherPredicateGroupForUsers = this.store.createFragment('predicates/predicate-group', {
      predicates: PredicateGroup.convertRawPredicates(this.store, rawPredicatesForUsers),
    });
  }

  get launcherPredicatesForAnonymous() {
    return this.args.visibilitySettings.launcherPredicatesForAnonymous;
  }

  get launcherPredicatesForUsers() {
    return this.args.visibilitySettings.launcherPredicatesForUsers;
  }

  get isUserType() {
    return this.messengerSettingsTargetUserProvider.isUserType;
  }

  get userAttributes() {
    return this.attributeService.filterableUserAttributesMaybeWithSegmentsAndTags.filter(
      (attribute: { isUntrackedCda: boolean }) => !attribute.isUntrackedCda,
    );
  }

  get companyAttributes() {
    return this.attributeService.filterableCompanyAttributesMaybeWithPlansSegmentsAndTags;
  }

  get messengerConditionalDisplayAttributes(): ArrayProxy<EmberObject & { name: string }> {
    return this.attributeService.messengerConditionalDisplayAttributes;
  }

  get attributeGroupList() {
    if (this.messengerSettingsTargetUserProvider.isUserType) {
      return this.attributeGroupListForUsers;
    }
    return this.attributeGroupListForVisitors;
  }

  get attributeGroupListForUsers() {
    return [
      {
        heading: this.intl.t('messenger.predicates.user-data'),
        attributes: [
          ...this.messengerConditionalDisplayAttributes.filter((el) => {
            return this.args.isMobilePlatform
              ? false
              : EXTRA_USER_ATTRIBUTES.includes(el.get('name'));
          }),
          ...this.userAttributes,
        ],
      },
      {
        heading: this.intl.t('messenger.predicates.company-data'),
        attributes: this.companyAttributes,
      },
    ];
  }

  get attributeGroupListForVisitors() {
    return [
      {
        heading: this.intl.t('messenger.predicates.person-data'),
        attributes: this.messengerConditionalDisplayAttributes,
      },
    ];
  }

  get messengerEnabled() {
    return this.args.visibilitySettings.messengerEnabled;
  }

  get launcherPredicates() {
    return this.args.visibilitySettings.launcherPredicates;
  }

  get isMessengerInTestMode() {
    if (!this.appService.app.canUseStandalone) {
      return false;
    }
    return (
      this.appService.mainStandalonePlatform.intercomMessengerConfig?.isMessengerInTestMode || false
    );
  }

  @action
  toggleMessengerEnabled() {
    this.args.visibilitySettings.messengerEnabled = !this.messengerEnabled;
  }

  @action
  updatePredicates(predicateGroup: PredicateGroup) {
    this.args.visibilitySettings.launcherPredicates.clear();
    let newPredicates = copy(predicateGroup);
    this.args.visibilitySettings.launcherPredicates.pushObjects(newPredicates);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Content::ShowLauncher': typeof ShowLauncherSettingsComponent;
    'messenger-settings/content/show-launcher': typeof ShowLauncherSettingsComponent;
  }
}
